import React, { createContext, useContext, useEffect } from 'react';
import { useNotificationWebSocket } from '@/hooks/useNotificationWebSocket';
import { AuthContext } from '@/context/AuthContext';
import { Notification } from '@/types/notification';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  reconnect: () => void;
  addRawMessageListener: (listener: (data: any) => void) => void;
  removeRawMessageListener: (listener: (data: any) => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const isAuthLoading = authContext?.isLoading ?? true;
  
  const {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    connectWebSocket,
    disconnect,
    addRawMessageListener,
    removeRawMessageListener
  } = useNotificationWebSocket({
    enableToast: true,
    enableNativeNotifications: localStorage.getItem('enableNativeNotifications') === 'true'
  });

  // Connect/disconnect based on authentication state
  useEffect(() => {
    if (!isAuthLoading && user && localStorage.getItem('token')) {
      if (!isConnected) {
        console.log('User authenticated and auth loaded, connecting notification WebSocket');
        connectWebSocket();
      }
    } else if (!user && !isAuthLoading) {
      console.log('User not authenticated, disconnecting notification WebSocket');
      disconnect();
    }
  }, [user, isAuthLoading, isConnected, connectWebSocket, disconnect]);

  const contextValue: NotificationContextType = {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    reconnect: connectWebSocket,
    addRawMessageListener,
    removeRawMessageListener
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
